import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useParams } from '@tanstack/react-router'
import { SharedPolicyDetailPage } from '@/routes/(protected)/workspaces/$workspaceId/policies_.$policySlug'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { useAuditorCenterCycle } from '@/components/auditor-center/AuditorCenterCycleShell'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/auditor-center/$auditCycleId_/policies/$policySlug'
)({
  component: AuditorPolicyDetailRoutePage,
})

function AuditorPolicyDetailRoutePage() {
  const { workspaceId, auditCycleId, policySlug } = useParams({
    from: '/(protected)/workspaces/$workspaceId/auditor-center/$auditCycleId_/policies/$policySlug',
  })
  const queryClient = useQueryClient()
  const { isAuditor } = useWorkspacePermissions(workspaceId)
  const { isAuditorReadOnly } = useAuditorCenterCycle()
  const detailQuery = useQuery(
    trpc.auditor.getPolicyDetails.queryOptions(
      {
        auditCycleId,
        policySlug,
      },
      { retry: false }
    )
  )
  const reviewMutation = useMutation(
    trpc.auditor.reviewPolicySubmission.mutationOptions()
  )

  if (!detailQuery.isLoading && (detailQuery.isError || !detailQuery.data)) {
    return (
      <div className='text-text-subtle flex h-full items-center justify-center text-[14px]'>
        Approved policy not found.
      </div>
    )
  }

  const handleReviewSubmission =
    isAuditorReadOnly || !isAuditor
      ? undefined
      : (decision: 'APPROVED' | 'NEEDS_REVISION', comments?: string) => {
          if (!detailQuery.data) return
          reviewMutation.mutate(
            {
              auditCycleId,
              policyDocumentId: detailQuery.data.policyDocumentId,
              policyDocumentVersionId: detailQuery.data.policyDocumentVersionId,
              decision,
              comments,
            },
            {
              onSuccess: () => {
                toast.success(
                  decision === 'APPROVED'
                    ? 'Policy approved for this audit'
                    : 'Changes requested for this policy'
                )
                queryClient.invalidateQueries({
                  queryKey: trpc.auditor.getPolicyDetails.queryKey({
                    auditCycleId,
                    policySlug,
                  }),
                })
                queryClient.invalidateQueries({
                  queryKey: trpc.auditor.getPolicies.queryKey({
                    auditCycleId,
                  }),
                })
                queryClient.invalidateQueries({
                  queryKey: trpc.auditor.listPolicySubmissions.queryKey({
                    auditCycleId,
                  }),
                })
                queryClient.invalidateQueries({
                  queryKey: trpc.auditor.getTimeline.queryKey(),
                })
                queryClient.invalidateQueries({
                  queryKey: trpc.auditor.listAuditorActivity.queryKey({
                    auditCycleId,
                  }),
                })
              },
              onError: (error) => {
                toast.error(error.message ?? 'Failed to review policy')
              },
            }
          )
        }

  return (
    <SharedPolicyDetailPage
      workspaceId={workspaceId}
      policySlug={policySlug}
      mode='readonly'
      auditCycleId={auditCycleId}
      backHref={`/workspaces/${workspaceId}/auditor-center/${auditCycleId}/policies`}
      auditorPolicy={detailQuery.data}
      submissionMeta={detailQuery.data}
      submissionMetaLoading={detailQuery.isLoading}
      onReviewSubmission={handleReviewSubmission}
      reviewSubmissionPending={reviewMutation.isPending}
      enableComments={false}
      hideCommentComposer={isAuditorReadOnly || !isAuditor}
    />
  )
}
